import { useAuthContext } from '@/context/auth/useAuthContext';
import { TCustomAxiosError } from '@/services/api';
import {
  contentCreatorApplicationPhotoPost,
  publicContentCreatorApplicationPhotoPost,
} from '@/services/api/requests/contentCreatorApplications';
import { useCreatorApplicationStore } from '@/store/useCreatorApplicationStore';
import { useMutation } from '@tanstack/react-query';
import useToast from '../useToast';

const useContentCreatorApplicationPhotoUpload = () => {
  const toast = useToast();
  const { isAuthenticated } = useAuthContext();
  const publicCredentials = useCreatorApplicationStore((state) => state.publicCredentials);

  const { isPending, mutate } = useMutation<unknown, TCustomAxiosError | Error, File>({
    mutationFn: async (file: File) => {
      if (isAuthenticated) {
        return contentCreatorApplicationPhotoPost(file);
      }

      if (!publicCredentials?.email || !publicCredentials.token) {
        throw new Error('Missing content creator application credentials.');
      }

      return publicContentCreatorApplicationPhotoPost({ file, publicCredentials });
    },
    onError: (error: TCustomAxiosError | Error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : error?.errors?.[0]?.message || 'An error occurred while uploading your profile photo.';

      toast({
        status: 'error',
        title: 'Photo Upload Failed',
        description: errorMessage,
      });
    },
  });

  return { isPending, mutate };
};

export default useContentCreatorApplicationPhotoUpload;
